<script lang="ts" setup>
import WebsiteButton from '@/components/WebsiteButton.vue';
import PhoneButton from '@/components/PhoneButton.vue';
import PostalAddress from '@/components/PostalAddress.vue';
import { getBuilder, getBuilderProjects, type GetBuilderResponse } from '@/api';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SectionHeader from '@/components/SectionHeader.vue';
import SearchResult from '@/views/search/SearchResult.vue';
import { useHead, type ResolvableLink, type ResolvableScript } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { getCanonicalUrl } from '@/utilities';
import BuilderMap from '@/views/directory/BuilderMap.vue';
import BuilderArticles from '@/views/directory/BuilderArticles.vue';
import { useTracking } from '@/utilities/tracking.ts';
import { buildHreflangLinks, buildSocialMeta } from '@/utilities/seo.ts';
import { useBuilderJsonSchema, useEditorialJsonSchema } from '@/utilities/json-ld.ts';
import { getAlternateLink } from '@/plugins/router/localized.ts';
import { useSsgState } from '@/utilities/ssg-state.ts';

const props = defineProps<{
  builderId: number;
}>();

const { trackPageView } = useTracking();
const { t } = useI18n();
const route = useRoute();
const canonicalUrl = computed(() => getCanonicalUrl(route.path));

const builder = ref<GetBuilderResponse | null>(null);
const projects = ref<number[]>([]);
const { generateBuilderJsonSchema } = useBuilderJsonSchema();
const { generateEditorialBreadcrumbJsonSchema } = useEditorialJsonSchema();

// La catégorie arrive avec une majuscule (« Constructeur ») : elle s'insère au milieu d'une phrase.
const lowercaseFirst = (value: string) => (value ? value.charAt(0).toLowerCase() + value.slice(1) : '');

// L'adresse d'un constructeur est souvent celle d'un secteur (« Parc Extension ») plutôt que d'une
// ville connue. Le titre reste sur la ville, la description ajoute la région, bien plus recherchée.
const subject = computed(() => {
  if (!builder.value) return '';

  const values = { name: builder.value.name, category: lowercaseFirst(builder.value.category.name), city: builder.value.address.city.name };

  return values.city ? t('views.builder.subject-in-city', values) : t('views.builder.subject', values);
});

// Google tronque la description autour de 160 caractères : on coupe nous-mêmes, sur un mot entier.
const MetaDescriptionLength = 160;

const truncate = (value: string, max: number) => {
  if (value.length <= max) return value;

  const cut = value.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');

  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.]+$/, '')}…`;
};

// La description ajoute la région, bien plus recherchée qu'un secteur, là où le titre manque de place.
const detailedSubject = computed(() => {
  if (!builder.value) return '';

  const values = { name: builder.value.name, category: lowercaseFirst(builder.value.category.name), city: builder.value.address.city.name, region: builder.value.address.region.name };

  if (values.city && values.region) return t('views.builder.subject-in-city-region', values);
  if (values.region) return t('views.builder.subject-in-region', values);
  if (values.city) return t('views.builder.subject-in-city', values);

  return t('views.builder.subject', values);
});

const metaDescription = computed(() => {
  if (!builder.value) return '';

  const summary = (builder.value.description ?? '').toPlainText().replace(/\s+/g, ' ').trim();

  if (!summary) {
    return t('views.builder.meta.description', { subject: detailedSubject.value });
  }

  const remaining = MetaDescriptionLength - detailedSubject.value.length - 2;

  return t('views.builder.meta.description-with-summary', { subject: detailedSubject.value, summary: truncate(summary, Math.max(remaining, 0)) });
});

const alternateLink = computed(() => getAlternateLink(route) ?? null);

// Un seul numéro remonte en tête de fiche ; les autres restent dans les coordonnées.
// Catégorie et localisation sur une ligne, sous le nom
const headerSubtitle = computed(() => {
  if (!builder.value) return '';

  const place = [builder.value.address.city.name, builder.value.address.region.name].filter((value) => value.length > 0).join(', ');

  return [builder.value.category.name, place].filter((value) => value.length > 0).join(' \u00b7 ');
});

const primaryPhone = computed(() => {
  for (const contact of builder.value?.contacts ?? []) {
    const number = contact.telephone || contact.cellular;
    if (number) return number;
  }

  return null;
});

// Répéter les coordonnées n'a d'intérêt que s'il y a plusieurs interlocuteurs, ou qu'ils sont nommés.
const hasDetailedContacts = computed(() => {
  const contacts = builder.value?.contacts ?? [];

  return contacts.length > 1 || contacts.some((contact) => contact.name.length > 0);
});

const hasAddress = computed(() => {
  const address = builder.value?.address;

  return !!address && (address.addressLine1.length > 0 || address.addressLine2.length > 0 || address.city.name.length > 0 || address.postalCode.length > 0);
});

const breadcrumbJsonLd = computed(() => {
  if (!builder.value) return null;

  return generateEditorialBreadcrumbJsonSchema(canonicalUrl.value, [
    { name: t('views.builder.breadcrumb.home'), to: { name: 'home' } },
    { name: t('views.builder.breadcrumb.directory'), to: { name: 'directory' } },
    { name: builder.value.name, to: { name: 'builder', params: { slug: builder.value.name.toSlug(), builderId: builder.value.id } } },
  ]);
});

const loadBuilder = async () => {
  const result = await getBuilder({
    path: {
      builderId: props.builderId,
    },
  });
  if (!result.data) {
    throw new Error(`Failed to fetch builder. ${JSON.stringify(result.error)}`);
  }
  builder.value = result.data;

  const projectsResult = await getBuilderProjects({
    path: {
      builderId: props.builderId,
    },
  });
  if (!projectsResult.data) {
    throw new Error(`Failed to fetch builder projects. ${JSON.stringify(projectsResult.error)}`);
  }
  projects.value = projectsResult.data.items;
};

const { ready } = useSsgState({
  key: `builder:${props.builderId}`,
  refs: { builder, projects },
  isLoaded: () => builder.value !== null,
  load: loadBuilder,
});

watch(
  () => props.builderId,
  () => {
    void loadBuilder();
    void trackPageView('builder', props.builderId);
  },
  { immediate: false }
);

onMounted(async () => {
  await ready();
  await trackPageView('builder', props.builderId);
});

useHead({
  title: () => (builder.value ? t('views.builder.title', { subject: subject.value }) : ''),
  meta: () => {
    if (!builder.value) return [];

    return buildSocialMeta({
      title: subject.value,
      description: metaDescription.value,
      summary: metaDescription.value,
      canonicalUrl: canonicalUrl.value,
      siteName: t('global.site-name'),
      image: builder.value.logo,
    });
  },
  link: () => {
    const links: ResolvableLink[] = [{ rel: 'canonical', href: canonicalUrl.value }];
    links.push(...buildHreflangLinks(canonicalUrl.value, alternateLink.value));

    return links;
  },
  script: (): ResolvableScript[] => {
    if (!builder.value) return [];

    const scripts: ResolvableScript[] = [{ type: 'application/ld+json', innerHTML: generateBuilderJsonSchema(builder.value, canonicalUrl.value) }];

    if (breadcrumbJsonLd.value) {
      scripts.push({ type: 'application/ld+json', innerHTML: breadcrumbJsonLd.value });
    }

    return scripts;
  },
});
</script>

<template>
  <article v-if="builder" class="space-y-8 md:space-y-12">
    <nav class="text-sm text-gray-500">
      <ol class="flex flex-wrap items-center gap-x-2">
        <li>
          <RouterLink :to="{ name: 'directory' }" class="hover:text-ph-green-600">
            {{ t('views.builder.breadcrumb.directory') }}
          </RouterLink>
        </li>
        <li>/</li>
      </ol>
    </nav>

    <!-- Identité, puis les actions : de quoi joindre le constructeur sans faire défiler la page -->
    <section class="space-y-4">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-xl font-bold tracking-tight text-gray-900 md:text-2xl lg:text-3xl">{{ builder.name }}</h1>
          <p class="text-xs text-gray-700 md:text-sm lg:text-base">{{ headerSubtitle }}</p>
          <p v-if="builder.rbqLicense.length > 0" class="text-xs text-gray-700 md:text-sm lg:text-base">{{ t('views.builder.rbq-license', { license: builder.rbqLicense }) }}</p>
        </div>
        <!-- Largeur bornée : sans elle, un logo horizontal s'étire et écrase le nom de l'entreprise -->
        <img v-if="builder.logo" :alt="builder.name" :src="builder.logo.resizedToMedium(true)" class="h-12 max-w-32 shrink-0 object-contain md:h-16 md:max-w-40" />
      </div>

      <div v-if="primaryPhone || (builder.isPaid && builder.website)" class="flex flex-wrap gap-2">
        <PhoneButton v-if="primaryPhone" :phone-number="primaryPhone" :target-id="builderId" target="builder" />
        <WebsiteButton v-if="builder.isPaid && builder.website" :target-id="builderId" :url="builder.website" target="builder" />
      </div>
    </section>

    <!-- Les projets d'abord : c'est ce que le visiteur est venu voir, quand il y en a -->
    <section v-if="projects.length > 0">
      <SectionHeader>{{ t('views.builder.projects') }}</SectionHeader>
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-6 p-1 md:grid-cols-3 lg:grid-cols-4" role="list">
        <template v-for="(projectId, index) in projects" :key="projectId">
          <SearchResult :index="index" :project-id="projectId" />
        </template>
      </ul>
    </section>

    <section v-if="builder.description">
      <SectionHeader>{{ t('views.builder.about') }}</SectionHeader>
      <div class="markdown" v-html="builder.description.toHtmlContent()" />
    </section>

    <BuilderArticles :builder-id="builderId" :builder-name="builder.name" />

    <section>
      <SectionHeader>{{ t('views.builder.contact-details') }}</SectionHeader>
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-start">
        <div class="space-y-4">
          <PostalAddress v-if="hasAddress" :address="builder.address" :address-line1-title="builder.addressLine1Title" :address-line2-title="builder.addressLine2Title" class="text-sm" />

          <div v-if="hasDetailedContacts" class="space-y-4">
            <div v-for="(contact, index) in builder.contacts" :key="index">
              <h3 v-if="contact.name.length > 0" class="mb-1 font-semibold">{{ t('views.builder.contact', { name: contact.name }) }}</h3>
              <div v-if="contact.telephone && contact.telephone.length > 0" class="mb-1">
                <PhoneButton :phone-number="`${contact.telephone}${contact.extension}`" :target-id="builderId" target="builder" />
              </div>
              <div v-if="contact.cellular && contact.cellular.length > 0" class="mb-1">
                <PhoneButton :phone-number="contact.cellular" :target-id="builderId" target="builder" />
              </div>
            </div>
          </div>
        </div>

        <BuilderMap :builder="builder" />
      </div>
    </section>

    <!-- Sortie vers les constructeurs actifs : sans elle, la fiche est un cul-de-sac -->
    <section class="flex justify-center">
      <RouterLink
        :to="{ name: 'builders' }"
        class="border-ph-green-600 text-ph-green-700 hover:bg-ph-green-50 focus-visible:outline-ph-green-600 rounded-lg border-2 px-6 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        {{ t('views.builder.see-active-builders') }}
      </RouterLink>
    </section>
  </article>
</template>

<style lang="css" scoped></style>
